import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Trash2, RefreshCw, Copy, Key, Shield, AlertTriangle, X, Search, Terminal, Link2 } from 'lucide-react';
import {
  integrationsApi,
  type UpdateIntegrationData,
} from '../../api/endpoints/integrations';
import { clientsApi } from '../../api/endpoints/clients';
import { queryClient } from '../../providers/QueryProvider';
import { Card, StatCard } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatDate } from '../../utils/formatters';
import { useDebounce } from '../../hooks/useDebounce';

const AVAILABLE_SCOPES = [
  { value: 'read', label: 'Read', description: 'View clients and logs' },
  { value: 'write', label: 'Write', description: 'Register/update clients' },
  { value: 'logs', label: 'Logs', description: 'Create log entries' },
  { value: 'admin', label: 'Admin', description: 'Full access' },
];

function scopeBadgeVariant(scope: string) {
  switch (scope) {
    case 'admin': return 'red' as const;
    case 'write': return 'yellow' as const;
    case 'logs': return 'purple' as const;
    default: return 'blue' as const;
  }
}

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'active': return 'green' as const;
    case 'pending': return 'yellow' as const;
    case 'revoked': return 'red' as const;
    default: return 'gray' as const;
  }
}

function typeBadgeVariant(type: string) {
  switch (type) {
    case 'tunnel': return 'blue' as const;
    case 'api': return 'purple' as const;
    case 'webhook': return 'green' as const;
    default: return 'gray' as const;
  }
}

export function IntegrationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Edit form state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editScopes, setEditScopes] = useState<string[]>([]);
  const [editAllowedIps, setEditAllowedIps] = useState('');

  // Regenerate key state
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Add client state
  const [showAddClient, setShowAddClient] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const debouncedClientSearch = useDebounce(clientSearch);

  const { data, isLoading } = useQuery({
    queryKey: ['integration', id],
    queryFn: () => integrationsApi.get(id!),
    enabled: !!id,
  });

  // Fetch the integration's associated clients
  const { data: clientsData } = useQuery({
    queryKey: ['integration-clients', id, data?.integration?.client_ids],
    queryFn: () => clientsApi.list({ limit: 200 }),
    enabled: !!data?.integration?.client_ids && data.integration.client_ids.length > 0,
  });

  // Fetch all clients for "add client" search
  const { data: searchClientsData } = useQuery({
    queryKey: ['clients-search', debouncedClientSearch],
    queryFn: () => clientsApi.list({ search: debouncedClientSearch, limit: 50 }),
    enabled: showAddClient,
  });

  const integration = data?.integration;

  // Filter associated clients from the full clients list
  const associatedClients = clientsData?.clients?.filter(
    c => integration?.client_ids?.includes(c.client_id),
  ) || [];

  // Filter search results to exclude already-associated clients
  const availableClients = searchClientsData?.clients?.filter(
    c => !integration?.client_ids?.includes(c.client_id),
  ) || [];

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateIntegrationData) => integrationsApi.update(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', id] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => integrationsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      navigate('/integrations');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => integrationsApi.revoke(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', id] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  const regenerateKeyMutation = useMutation({
    mutationFn: () => integrationsApi.regenerateKey(id!),
    onSuccess: (result) => {
      setNewApiKey(result.api_key);
      setShowRegenerateConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['integration', id] });
    },
  });

  const addClientMutation = useMutation({
    mutationFn: (clientId: string) => integrationsApi.addClient(id!, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', id] });
      queryClient.invalidateQueries({ queryKey: ['integration-clients', id] });
    },
  });

  const removeClientMutation = useMutation({
    mutationFn: (clientId: string) => integrationsApi.removeClient(id!, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', id] });
      queryClient.invalidateQueries({ queryKey: ['integration-clients', id] });
    },
  });

  function startEditing() {
    if (!integration) return;
    setEditName(integration.name);
    setEditDescription(integration.description || '');
    setEditScopes([...integration.scopes]);
    setEditAllowedIps((integration.allowed_ips || []).join(', '));
    setEditing(true);
  }

  function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    updateMutation.mutate({
      name: editName,
      description: editDescription,
      scopes: editScopes,
      allowed_ips: editAllowedIps ? editAllowedIps.split(',').map(ip => ip.trim()).filter(Boolean) : [],
    });
  }

  function toggleEditScope(scope: string) {
    setEditScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope],
    );
  }

  async function copyToClipboard(text: string, field: string) {
    await navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  }

  if (isLoading) return <PageSpinner />;

  if (!integration) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Integration not found</p>
        <Link to="/integrations" className="text-purple-600 text-sm mt-2 inline-block">Back to integrations</Link>
      </div>
    );
  }

  const isExpired = integration.expires_at && new Date(integration.expires_at) < new Date();

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/integrations')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">{integration.name}</h1>
              <Badge variant={statusBadgeVariant(integration.status)} dot>{integration.status}</Badge>
              <Badge variant={typeBadgeVariant(integration.type)}>{integration.type}</Badge>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{integration.description || 'No description'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!editing && (
            <button
              onClick={startEditing}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Edit
            </button>
          )}
          {integration.status === 'active' ? (
            <button
              onClick={() => { if (confirm('Revoke this integration? It will stop working immediately.')) revokeMutation.mutate(); }}
              className="px-3 py-1.5 bg-yellow-500 text-white rounded-md text-xs hover:bg-yellow-600 flex items-center gap-1"
            >
              <Shield size={14} />
              Revoke
            </button>
          ) : (
            <button
              onClick={() => { if (confirm('Delete this integration permanently?')) deleteMutation.mutate(); }}
              className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 flex items-center gap-1"
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Content - Left 2/3 */}
        <div className="lg:col-span-2 space-y-4">
          {/* Edit Form */}
          {editing && (
            <Card title="Edit Integration">
              <form onSubmit={handleSaveEdit} className="p-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    required
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-sm dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-sm dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Scopes</label>
                  <div className="space-y-2">
                    {AVAILABLE_SCOPES.map(scope => (
                      <label key={scope.value} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editScopes.includes(scope.value)}
                          onChange={() => toggleEditScope(scope.value)}
                          className="rounded text-purple-600 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {scope.label} <span className="text-gray-400 dark:text-gray-500">- {scope.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Allowed IPs <span className="text-gray-400 font-normal">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={editAllowedIps}
                    onChange={e => setEditAllowedIps(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-sm dark:bg-gray-700 dark:text-white"
                    placeholder="Leave empty for all"
                  />
                </div>

                {updateMutation.isError && (
                  <p className="text-xs text-red-600">{(updateMutation.error as Error).message}</p>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-4 py-1.5 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </Card>
          )}

          {/* Details Card */}
          <Card title="Integration Details">
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                ['Deployment ID', integration.deployment_id],
                ['Type', integration.type],
                ['Status', integration.status],
                ['Created', formatDate(integration.createdAt)],
                ['Updated', formatDate(integration.updatedAt)],
                ['Clients', String(integration.client_ids?.length || 0)],
              ].map(([label, value]) => (
                <div key={label as string}>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{label}</p>
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200 break-all">{String(value)}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Deployment Hash */}
          <Card title="Deployment Hash">
            <div className="p-4">
              <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg">
                <code className="text-[10px] text-gray-700 dark:text-gray-300 break-all">{integration.deployment_hash}</code>
              </div>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">Use this hash to verify deployment authenticity</p>
            </div>
          </Card>

          {/* Scopes */}
          <Card title="Permissions">
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {(integration.scopes || []).map(scope => (
                  <Badge key={scope} variant={scopeBadgeVariant(scope)}>{scope}</Badge>
                ))}
                {(!integration.scopes || integration.scopes.length === 0) && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">No scopes assigned</span>
                )}
              </div>
            </div>
          </Card>

          {/* IP Whitelist */}
          <Card title="IP Whitelist">
            <div className="p-4">
              {integration.allowed_ips && integration.allowed_ips.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {integration.allowed_ips.map((ip, i) => (
                    <code key={i} className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-xs text-gray-700 dark:text-gray-300">
                      {ip}
                    </code>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">No IP restrictions - all IPs allowed</p>
              )}
            </div>
          </Card>

          {/* Associated Clients */}
          <Card
            title={`Associated Clients (${integration.client_ids?.length || 0})`}
            action={
              <button
                onClick={() => { setShowAddClient(true); setClientSearch(''); }}
                className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium"
              >
                + Add Client
              </button>
            }
          >
            {associatedClients.length > 0 ? (
              <Table>
                <Thead>
                  <tr>
                    <Th>Status</Th>
                    <Th>Client</Th>
                    <Th>Deployment</Th>
                    <Th>Hostname</Th>
                    <Th>Last Seen</Th>
                    <Th>Actions</Th>
                  </tr>
                </Thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {associatedClients.map(client => (
                    <Tr key={client._id}>
                      <Td>
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                          client.status === 'connected' ? 'bg-green-500' :
                          client.status === 'disconnected' ? 'bg-red-500' :
                          'bg-yellow-500'
                        }`} />
                      </Td>
                      <Td>
                        <Link
                          to={`/clients/${client.client_id}`}
                          className="text-purple-600 dark:text-purple-400 hover:underline font-medium"
                          onClick={e => e.stopPropagation()}
                        >
                          {client.client_name || client.client_id}
                        </Link>
                        <p className="text-[10px] text-gray-400">{client.client_id}</p>
                      </Td>
                      <Td>
                        <span className="text-gray-600 dark:text-gray-400">{client.deployment_name || '-'}</span>
                      </Td>
                      <Td>
                        <span className="text-gray-600 dark:text-gray-400">{client.hostname || client.local_ip || '-'}</span>
                      </Td>
                      <Td>
                        <span className="text-gray-500 dark:text-gray-400">{formatDate(client.last_seen)}</span>
                      </Td>
                      <Td>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Remove ${client.client_name || client.client_id} from this integration?`)) {
                              removeClientMutation.mutate(client.client_id);
                            }
                          }}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                        >
                          Remove
                        </button>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">No clients registered with this integration</p>
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar - Right 1/3 */}
        <div className="space-y-4">
          {/* Usage Statistics */}
          <Card title="Usage Statistics">
            <div className="p-4 space-y-3">
              <StatCard label="Total Requests" value={integration.usage?.total_requests || 0} color="purple" />
              <div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Last Request</p>
                <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                  {integration.usage?.last_request ? formatDate(integration.usage.last_request) : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Last IP</p>
                <p className="text-xs font-mono text-gray-800 dark:text-gray-200">
                  {integration.usage?.last_ip || 'N/A'}
                </p>
              </div>
            </div>
          </Card>

          {/* Rate Limits */}
          <Card title="Rate Limits">
            <div className="p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">Per Minute</span>
                <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                  {integration.rate_limit?.requests_per_minute || 60}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">Per Hour</span>
                <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                  {integration.rate_limit?.requests_per_hour || 1000}
                </span>
              </div>
            </div>
          </Card>

          {/* Expiration */}
          <Card title="Expiration">
            <div className="p-4">
              {integration.expires_at ? (
                <div>
                  <p className={`text-xs font-medium ${isExpired ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'}`}>
                    {formatDate(integration.expires_at)}
                  </p>
                  {isExpired && (
                    <p className="text-[10px] text-red-600 dark:text-red-400 mt-1">This integration has expired</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">No expiration set</p>
              )}
            </div>
          </Card>

          {/* API Key Actions */}
          <Card title="API Key">
            <div className="p-4 space-y-3">
              {newApiKey ? (
                <div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-2 mb-3">
                    <div className="flex items-center gap-1">
                      <AlertTriangle size={12} className="text-yellow-600 dark:text-yellow-400" />
                      <p className="text-yellow-800 dark:text-yellow-300 text-[10px] font-medium">Save this key now!</p>
                    </div>
                  </div>
                  <div className="flex">
                    <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-2 py-1.5 rounded-l-md font-mono text-[10px] text-gray-800 dark:text-gray-200 overflow-x-auto break-all">
                      {newApiKey}
                    </code>
                    <button
                      onClick={() => copyToClipboard(newApiKey, 'newKey')}
                      className="bg-gray-200 dark:bg-gray-600 px-2 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center"
                    >
                      <Copy size={12} className={copied === 'newKey' ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'} />
                    </button>
                  </div>
                  <button
                    onClick={() => setNewApiKey(null)}
                    className="mt-2 text-[10px] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Dismiss
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">
                    API key is hashed and cannot be retrieved. Regenerate if needed.
                  </p>
                  <button
                    onClick={() => setShowRegenerateConfirm(true)}
                    className="w-full px-3 py-1.5 bg-yellow-500 text-white rounded-md text-xs hover:bg-yellow-600 flex items-center justify-center gap-1"
                  >
                    <Key size={14} />
                    Regenerate API Key
                  </button>
                </div>
              )}
            </div>
          </Card>

          {/* Client Registration URLs */}
          <Card title="Client Registration">
            <div className="p-4 space-y-3">
              {/* Register Client API URL */}
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <Link2 size={12} className="text-gray-400" />
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium">Register Client API</p>
                </div>
                <div className="flex">
                  <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-2 py-1.5 rounded-l-md font-mono text-[10px] text-gray-800 dark:text-gray-200 overflow-x-auto break-all">
                    {`${window.location.origin}/api/integrations/external/register-client`}
                  </code>
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}/api/integrations/external/register-client`, 'registerUrl')}
                    className="bg-gray-200 dark:bg-gray-600 px-2 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center"
                  >
                    <Copy size={12} className={copied === 'registerUrl' ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'} />
                  </button>
                </div>
              </div>

              {/* Install Script URL */}
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <Terminal size={12} className="text-gray-400" />
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium">Install Script</p>
                </div>
                <div className="flex">
                  <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-2 py-1.5 rounded-l-md font-mono text-[10px] text-gray-800 dark:text-gray-200 overflow-x-auto break-all">
                    {`${window.location.origin}/client-install.sh`}
                  </code>
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}/client-install.sh`, 'installUrl')}
                    className="bg-gray-200 dark:bg-gray-600 px-2 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center"
                  >
                    <Copy size={12} className={copied === 'installUrl' ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'} />
                  </button>
                </div>
              </div>

              {/* Quick Usage Example */}
              <div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-1">Quick Install Command</p>
                <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-3 relative group">
                  <pre className="text-[10px] text-green-400 font-mono whitespace-pre-wrap break-all leading-relaxed">{`curl -fsSL ${window.location.origin}/client-install.sh | sudo bash -s -- \\
  --dashboard-url ${window.location.origin} \\
  --api-key <YOUR_API_KEY> \\
  --server-host ${window.location.hostname}`}</pre>
                  <button
                    onClick={() => copyToClipboard(
                      `curl -fsSL ${window.location.origin}/client-install.sh | sudo bash -s -- --dashboard-url ${window.location.origin} --api-key <YOUR_API_KEY> --server-host ${window.location.hostname}`,
                      'installCmd'
                    )}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-700 hover:bg-gray-600 p-1 rounded"
                  >
                    <Copy size={10} className={copied === 'installCmd' ? 'text-green-400' : 'text-gray-400'} />
                  </button>
                </div>
              </div>

              {/* cURL Registration Example */}
              <div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium mb-1">cURL Register Example</p>
                <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-3 relative group">
                  <pre className="text-[10px] text-green-400 font-mono whitespace-pre-wrap break-all leading-relaxed">{`curl -X POST ${window.location.origin}/api/integrations/external/register-client \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"client_id":"store-001","client_name":"Store Name","port":2222}'`}</pre>
                  <button
                    onClick={() => copyToClipboard(
                      `curl -X POST ${window.location.origin}/api/integrations/external/register-client -H "Authorization: Bearer <YOUR_API_KEY>" -H "Content-Type: application/json" -d '{"client_id":"store-001","client_name":"Store Name","port":2222}'`,
                      'curlCmd'
                    )}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-700 hover:bg-gray-600 p-1 rounded"
                  >
                    <Copy size={10} className={copied === 'curlCmd' ? 'text-green-400' : 'text-gray-400'} />
                  </button>
                </div>
              </div>
            </div>
          </Card>

          {/* Webhook (if applicable) */}
          {integration.webhook_url && (
            <Card title="Webhook">
              <div className="p-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded-lg">
                  <p className="text-[10px] text-green-800 dark:text-green-400 font-medium">Webhook Active</p>
                  <p className="text-[10px] text-green-600 dark:text-green-500 break-all mt-1">{integration.webhook_url}</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Regenerate Key Confirmation Modal */}
      <Modal
        open={showRegenerateConfirm}
        onClose={() => setShowRegenerateConfirm(false)}
        title="Regenerate API Key"
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <p className="text-yellow-800 dark:text-yellow-300 text-xs">
                The old API key will stop working immediately. Any clients using this key will need to be updated.
              </p>
            </div>
          </div>

          {regenerateKeyMutation.isError && (
            <p className="text-xs text-red-600">{(regenerateKeyMutation.error as Error).message}</p>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowRegenerateConfirm(false)}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={() => regenerateKeyMutation.mutate()}
              disabled={regenerateKeyMutation.isPending}
              className="px-4 py-1.5 bg-yellow-500 text-white rounded-md text-sm hover:bg-yellow-600 disabled:opacity-50 flex items-center gap-1"
            >
              <RefreshCw size={14} className={regenerateKeyMutation.isPending ? 'animate-spin' : ''} />
              {regenerateKeyMutation.isPending ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Client Modal */}
      <Modal
        open={showAddClient}
        onClose={() => setShowAddClient(false)}
        title="Add Client to Integration"
        wide
      >
        <div className="space-y-4">
          <div className="relative">
            <Search size={16} className="absolute left-2.5 top-2 text-gray-400" />
            <input
              type="text"
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
              placeholder="Search clients by name, ID, hostname..."
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-800 rounded-md">
            {availableClients.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-800">
                {availableClients.map(client => (
                  <div
                    key={client._id}
                    className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                        client.status === 'connected' ? 'bg-green-500' :
                        client.status === 'disconnected' ? 'bg-red-500' :
                        'bg-yellow-500'
                      }`} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                          {client.client_name || client.client_id}
                        </p>
                        <p className="text-[10px] text-gray-400 truncate">{client.client_id}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => addClientMutation.mutate(client.client_id)}
                      disabled={addClientMutation.isPending}
                      className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium flex-shrink-0 ml-2"
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
                {clientSearch ? 'No matching clients found' : 'Search for clients to add'}
              </div>
            )}
          </div>

          {/* Currently added clients as tags */}
          {integration.client_ids && integration.client_ids.length > 0 && (
            <div>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-2">Currently assigned ({integration.client_ids.length})</p>
              <div className="flex flex-wrap gap-1">
                {associatedClients.map(client => (
                  <span
                    key={client.client_id}
                    className="inline-flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2 py-0.5 rounded-full text-[10px]"
                  >
                    {client.client_name || client.client_id}
                    <button
                      onClick={() => removeClientMutation.mutate(client.client_id)}
                      className="hover:text-red-600 dark:hover:text-red-400"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              onClick={() => setShowAddClient(false)}
              className="px-4 py-1.5 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
